import { useCallback, useRef, useEffect } from 'react';

interface FormulaData {
  functions: Array<{ name: string; type: string }>;
  otherBlocks: Array<{ blockname: string; indicator: string; type: string }>;
  dimensionFormula: Array<{ dimName: string; dimPropertie: string; type: string }>;
  dimensionItemsFormula: Array<{ dimName: string; dimItem: string; type: string }>;
  currBlockIndicator: Array<{ indName: string; type: string }>;
}

interface ProcessedFormulaData {
  combinedData: string[];
  suggestions: Array<{
    name?: string;
    blockname?: string;
    indicator?: string;
    dimName?: string;
    dimPropertie?: string;
    dimItem?: string;
    indName?: string;
    type: string;
  }>;
  regexPatterns: {
    functionRegex: string | null;
    blocknameRegex: string | null;
    indicatorRegex: string | null;
    dimensionRegex: string | null;
    dimensionPropertyRegex: string | null;
    dimensionNameRegex: string | null;
    dimensionItemRegex: string | null;
    currBlockIndicatorRegex: string | null;
    combinedRegexGlobal: string | null;
  };
}

interface WorkerResponse {
  success: boolean;
  data?: ProcessedFormulaData;
  error?: string;
}

const useFormulaWorker = () => {
  const workerReference = useRef<Worker | null>(null);
  const isProcessingReference = useRef(false);

  // Initialize worker
  useEffect(() => {
    if (typeof Window !== 'undefined' && 'Worker' in window) {
      try {
        workerReference.current = new Worker(
          new URL('../workers/formulaProcessingWorker.ts', import.meta.url),
          { type: 'module' },
        );
      } catch (error) {
        console.warn('Failed to create formula worker:', error);
      }
    }

    return () => {
      if (workerReference.current) {
        workerReference.current.terminate();
        workerReference.current = null;
      }
    };
  }, []);

  // Process formula data using worker
  const processFormulaData = useCallback((data: FormulaData): Promise<ProcessedFormulaData> => {
    return new Promise((resolve, reject) => {
      if (!workerReference.current) {
        // Fallback to synchronous processing if worker is not available
        reject(new Error('Worker not available'));
        return;
      }

      if (isProcessingReference.current) {
        reject(new Error('Worker is already processing'));
        return;
      }

      isProcessingReference.current = true;

      const handleMessage = (event: MessageEvent<WorkerResponse>) => {
        isProcessingReference.current = false;

        if (event.data.success && event.data.data) {
          resolve(event.data.data);
        } else {
          reject(new Error(event.data.error || 'Worker processing failed'));
        }

        // Clean up listener
        workerReference.current?.removeEventListener('message', handleMessage);
      };

      const handleError = (error: ErrorEvent) => {
        isProcessingReference.current = false;
        reject(new Error(`Worker error: ${error.message}`));

        // Clean up listeners
        workerReference.current?.removeEventListener('message', handleMessage);
        workerReference.current?.removeEventListener('error', handleError);
      };

      // Set up event listeners
      workerReference.current.addEventListener('message', handleMessage);
      workerReference.current.addEventListener('error', handleError);

      // Send data to worker
      workerReference.current.postMessage(data);

      // Set timeout for worker response
      setTimeout(() => {
        if (isProcessingReference.current) {
          isProcessingReference.current = false;
          reject(new Error('Worker timeout'));

          // Clean up listeners
          workerReference.current?.removeEventListener('message', handleMessage);
          workerReference.current?.removeEventListener('error', handleError);
        }
      }, 5000); // 5 second timeout
    });
  }, []);

  // Check if worker is available
  const isWorkerAvailable = useCallback(() => {
    return workerReference.current !== null;
  }, []);

  return {
    processFormulaData,
    isWorkerAvailable,
  };
};

export default useFormulaWorker;
